import {
  CollectionMembershipDocument,
  CollectionMembershipMutationResult,
  parseCollectionMembershipMutationResult,
} from '@qkindex/contracts';
import { createOperationId } from '../../domain/operationId';
import { leaveCollection } from '../../firebase/functions';
import { firebaseCollectionRepository } from '../../repositories/collectionRepository';

export interface CollectionLeaveDependencies {
  createOperationId(): string;
  getMembership(
    collectionId: string,
    userId: string,
  ): Promise<CollectionMembershipDocument | undefined>;
  leave(request: {
    collectionId: string;
    operationId: string;
    expectedRevision: number;
    expectedMembershipVersion: number;
    expectedRole: 'administrator' | 'editor' | 'viewer';
  }): Promise<CollectionMembershipMutationResult>;
}

const defaults = (): CollectionLeaveDependencies => ({
  createOperationId,
  getMembership: (collectionId, userId) =>
    firebaseCollectionRepository.getMembership(collectionId, userId),
  leave: async (request) =>
    parseCollectionMembershipMutationResult(
      (await leaveCollection(request)).data,
    ),
});

export const createCollectionLeaveController = (
  dependencies: CollectionLeaveDependencies = defaults(),
) => {
  let pending: { key: string; operationId: string } | undefined;
  return {
    async leave(
      collectionId: string,
      userId: string,
      role: 'administrator' | 'editor' | 'viewer',
    ) {
      const membership = await dependencies.getMembership(collectionId, userId);
      if (
        !membership ||
        membership.collectionId !== collectionId ||
        membership.userId !== userId ||
        membership.status !== 'active' ||
        membership.role !== role
      ) {
        throw new Error(
          'Collection membership changed. Refresh and try again.',
        );
      }
      const key = `${collectionId}\0${userId}\0${membership.revision}\0${membership.membershipVersion}\0${role}`;
      if (!pending || pending.key !== key) {
        pending = { key, operationId: dependencies.createOperationId() };
      }
      return dependencies.leave({
        collectionId,
        operationId: pending.operationId,
        expectedRevision: membership.revision,
        expectedMembershipVersion: membership.membershipVersion,
        expectedRole: role,
      });
    },
    acknowledge() {
      pending = undefined;
    },
    reset() {
      pending = undefined;
    },
  };
};

const nestedCode = (error: unknown, depth = 0): string | undefined => {
  if (!error || typeof error !== 'object' || depth > 3) return undefined;
  const value = error as {
    code?: unknown;
    details?: { code?: unknown };
    cause?: unknown;
  };
  if (typeof value.details?.code === 'string') return value.details.code;
  return (
    nestedCode(value.cause, depth + 1) ||
    (typeof value.code === 'string' ? value.code : undefined)
  );
};

export const collectionLeaveErrorMessage = (error: unknown): string => {
  const code = nestedCode(error);
  if (code?.includes('operation-conflict')) {
    return 'Your membership changed. Refresh and try again.';
  }
  if (
    code?.includes('role-required') ||
    code?.includes('membership-required')
  ) {
    return 'This collection membership is no longer active.';
  }
  if (code?.includes('collection-unavailable')) {
    return 'Leaving is not available while the collection is changing.';
  }
  if (
    error instanceof Error &&
    error.message.startsWith('Collection membership changed.')
  ) {
    return error.message;
  }
  return 'Unable to leave the collection. Check your connection and try again.';
};
